import moment from 'moment';
import { DataFormatter } from '../dataformats/DataFormatter';
import { Buffer } from 'buffer';
import { LogicEngine } from '../LogicEngine/LogicEngine';

/**
 Common tools to handle data better
 @category Utility Classes
 */

// This just adds a little bit of protection against very casual viewing or adjusting the strings in transit
const superSimpleInsecureKey = Buffer.from('LainaPlatformShared');

const globalBuffer = global.Buffer || require("buffer").Buffer;

export class DataTools
{
	/** Simple enocder, Object or value to base64 */
	public static encodeObject(obj: any)
	{
		const strValue = DataTools.toJSON(obj);
		const b64 = Buffer.from(strValue).toString('base64');
		return b64;
	}

	/** Simple enocder, Object or value to base64 */
	public static encodeObjectCompressed(obj: any)
	{
		const strValue = DataTools.toJSON(obj);
		return '_' + Buffer.from(strValue, 'binary').toString('base64');
	}

	/** Simple decoder, base64 to object/value */
	public static decodeObject(value: string): any
	{
		try
		{
			if (value && value.charAt(0) === '_')
			{
				// Compressed value
				value = value.substring(1);
				const jsonString = Buffer.from(value, 'base64').toString("utf8");
				const obj = DataTools.fromJSON(jsonString);
				return obj;
			} else
			{
				const bytes = Buffer.from(value, 'base64');
				const strValue = bytes.toString('utf-8');
				const obj = DataTools.fromJSON(strValue);
				return obj;
			}
		} catch {
			console.warn('Error decoding object:', value);
			return {};
		}
	}

	/**
	 * All-purpose exception reporting
	 * @param strLocation A string value for error location
	 * @param e A value to represent error
	 * */
	public static reportException(strLocation: string, e: any)
	{
		if (e instanceof Error)
		{
			// Type information available, handle error
			console.error(`Exception reported in "${strLocation}" type Error:`, e.message);
			console.log(e.stack);
		} else if (typeof e === 'string')
		{
			console.error(`Exception reported in "${strLocation}" type string:`, e);
		} else if (typeof e === 'number')
		{
			console.error(`Exception reported in "${strLocation}" type number:`, e);
		} else
		{
			console.error(`Exception reported in "${strLocation}" type unknown:`, e);
		}
	}

	/**
	 A better version of stringify that will store additional types of data.
	 Date values are serialized with the timezone information.

	 let strJson = DataTools.toJSON(o);
	 @param o A value to stringify
	 @returns A stringified string value
	 */
	public static toJSON(o: any): string
	{
		function replacer(this: any, key: string, value: any): any
		{
			let v = this[key];

			if (v instanceof Date)
			{
				return { $date: v.toISOString(), $tzOffset: v.getTimezoneOffset() };
			}

			if (v instanceof RegExp)
			{
				return { $regex: v.source, $optins: v.flags };
			}

			return v;
		}

		const r = JSON.stringify(o, replacer, 4);
		return r;
	}

	/**
	 A replacement for JSON.parse() that handles converting complext data
	 types into an object.

	 @param o [string] The JSON text that needs to be parsed

	 @return [any] The object or result of the parsed JSON.

	 TODO: Handle try/catch for failure to parse JSON nicely.
	 */
	public static fromJSON(o: string): any
	{
		function reviver(this: any, key: string, value: any): any
		{
			if (value && value.$date)
			{
				let dt = new Date(value.$date);
				// Todo: Do something with the original timzeone
				return dt;
			}

			return value;
		}

		const r = JSON.parse(o, reviver);
		return r;
	}

	/**
	 * Get the current date/time with an option number of seconds of timezone offset
	 * @param timezone_offset_seconds
	 * @returns
	 */
	static getCurrentDateWithOffset(timezone_offset_seconds: number | undefined)
	{
		//  If the parameter is not used, we are using the client local time
		if (timezone_offset_seconds === undefined || timezone_offset_seconds === null)
		{
			return new Date();
		}

		if (typeof timezone_offset_seconds === 'string')
			timezone_offset_seconds = parseInt(timezone_offset_seconds, 10);

		let now = new Date();

		// If the current local client timezone is 120
		// and we want 240 then we still need 2 more hours
		// so 120 - 240 = -120
		let currentOffset = now.getTimezoneOffset();
		let diffSeconds = currentOffset - timezone_offset_seconds;
		now.setTime(now.getTime() + diffSeconds * 60000);
		return now;
	}

	/**
	 * given a string, return number or null
	 * Given a "number" object returns that number
	 * Given a date, returns the time in epoch
	 *
	 * @param [any] data to be converted to number
	 * @return [Float] result equalent version of string in float
	 */
	static getNumber(data: any): number | null
	{
		// should return null if null, undefined or boolean is given
		if (data === null || data === undefined || typeof data === 'boolean')
		{
			return null;
		}

		if (typeof data === 'number')
		{
			return data;
		}

		if (typeof data === 'object' && data.getTime)
		{
			return data.getTime();
		}

		// Test for a variety of known acceptable date formats
		if (typeof data === 'string')
		{
			let m: moment.Moment | null | undefined = undefined;

			if (
				/\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)/.test(
					data,
				)
			)
			{
				m = DataTools.getMoment(data);
				if (m) return m.toDate().getTime();
			}

			if (data.match(/\d\d\d\d-\d\d-\d\d[ T]\d\d:\d\d:\d\d/))
			{
				m = DataTools.getMoment(data);
				if (m) return m.toDate().getTime();
			}

			if (data.match(/\d\d\d\d\.\d\d\.\d\d \d\d:\d\d:\d\d/))
			{
				m = DataTools.getMoment(data);
				if (m) return m.toDate().getTime();
			}

			if (
				data.match(/\d\d-\d\d-\d\d\d\d \d\d:\d\d:\d\d/) ||
				data.match(/\d\d\/\d\d\/\d\d\d\d \d\d:\d\d:\d\d/) ||
				data.match(/..., \d\d\d\d \d\d:\d\d:\d\d/) ||
				data.match(/..., \d\d\d\d \d:\d\d:\d\d/) ||
				data.match(/..., \d\d\d\d$/)
			)
			{
				m = DataTools.getMoment(data);
				if (m) return m.toDate().getTime();
			}

			if (
				data.match(/\d\d\d\d\.\d\d\.\d\d/) ||
				data.match(/\d\d-\d\d-\d\d\d\d/) ||
				data.match(/\d\d\d\d-\d\d-\d\d/) ||
				data.match(/\d{1,2}\/\d{1,2}\/\d\d\d\d/)
			)
			{
				m = DataTools.getMoment(data);
				if (m)
				{
					let d = m?.toDate();
					d.setHours(12, 0, 0);
					return d.getTime();
				}
			}
		}

		const m = data.toString().match(/(\d+)\s*%/);
		if (m)
		{
			return parseFloat(m[1]) / 100.0;
		}

		let result = data.toString().replace(/[^0-9\.\-]/g, '');
		result = parseFloat(result);
		if (isNaN(result))
		{
			return null;
		}

		return result;
	}

	static makeMoment(date: string, format?: string): moment.Moment
	{
		return moment(date, format);
	}

	/**
	 * Given a date in a human readable form, parse it and return the Moment
	 * Attempts to parse a variety of formats.
	 * object (see momentjs) that represents the date/time.
	 *
	 * @param [string] date The date string to parse
	 * @note returns null if the date is invalid
	 * @return [Moment] moment object
	 */
	static getMoment(data: any, format?: any): moment.Moment | null
	{
		try
		{
			let m;
			if (data == null)
			{
				return null;
			}

			if (data._isAMomentObject != null && data._isAMomentObject)
			{
				return data;
			}

			if (typeof data === 'object' && data.getTime != null && typeof data.getTime === 'function')
			{
				m = moment(data);
				return m;
			}

			if (typeof data === 'number')
			{
				if (data > 53280000)
				{
					const d = new Date(data);
					return moment(d);
				}

				// console.warn('Passing getMoment a number not in range:', data);
				return null;
			}

			if (typeof data === 'string')
			{
				if (data.match(/\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)/))
				{
					return this.makeMoment(data);
				}

				if (data.match(/\d\d\d\d-\d\d-\d\d[ T]\d\d:\d\d:\d\d/))
				{
					return this.makeMoment(data, 'YYYY-MM-DD HH:mm:ss');
				}

				if (data.match(/\d\d\d\d\.\d\d\.\d\d \d\d:\d\d:\d\d/))
				{
					return this.makeMoment(data, 'YYYY-MM-DD HH:mm:ss');
				}

				if (data.match(/\d\d\d\d\.\d\d\.\d\d/))
				{
					return this.makeMoment(data, 'YYYY-MM-DD');
				}

				if (data.match(/\d\d-\d\d-\d\d\d\d \d\d:\d\d:\d\d/))
				{
					return this.makeMoment(data, 'MM-DD-YYYY HH:mm:ss');
				}

				if (data.match(/\d\d-\d\d-\d\d\d\d/))
				{
					return this.makeMoment(data, 'MM-DD-YYYY');
				}

				if (data.match(/\d\d\d\d-\d\d-\d\d/))
				{
					return this.makeMoment(data, 'YYYY-MM-DD');
				}

				if (data.match(/\d\d\/\d\d\/\d\d\d\d \d\d:\d\d:\d\d/))
				{
					return this.makeMoment(data, 'MM/DD/YYYY HH:mm:ss');
				}

				if (data.match(/\d{1,2}\/\d{1,2}\/\d\d\d\d/))
				{
					return this.makeMoment(data, 'MM/DD/YYYY');
				}

				if (data.match(/..., \d\d\d\d \d\d:\d\d:\d\d/))
				{
					return this.makeMoment(data, 'ddd, MMM Do, YYYY h:mm:ss a');
				}

				if (data.match(/..., \d\d\d\d \d:\d\d:\d\d/))
				{
					return this.makeMoment(data, 'ddd, MMM Do, YYYY h:mm:ss a');
				}

				// data in format of "Tue, Jul 9th, 2019"
				if (data.match(/..., \d\d\d\d$/))
				{
					return this.makeMoment(data, 'ddd, MMM Do, YYYY');
				}

				if (typeof data === 'object' && data['$date'] != null)
				{
					return moment(new Date(data['$date']));
				}

				if (/16[0-9]{10,13}/.test(data.toString()))
				{
					const d = new Date(parseInt(data.toString(), 10));
					return moment(d);
				}

				if (data.length < 10) return null;

				console.log('DataTools Warning: Attempting to getDate from unknown:', data, typeof data, "Command=", LogicEngine.currentCommandText);
				m = moment(data, format);
				if (m != null && m.isValid() === true)
				{
					return m;
				}
			}

			// unsupported type?
			return null;
		} catch (e)
		{
			console.log('DataTools getMoment Unable to get date from [', data, ']');
			DataTools.reportException('getMoment from value', e);
		}

		return null;
	}

	/**
	 Given some input, returns an object that is usable as a list of options.

	 Passing a comma seperated string: "Apple, Pear, Grape"
	 Returns ["Apple", "Pear", "Grape"]

	 Passing an object in string format such as "{ happy: 1 }"
	 Returns the value as an object

	 Passing an object such as { "A" : "Apple", "B" : "Ball" }
	 Returns the same object sent in without change.

	 Passing an array object such as ["Apple", "Ball", "Grape"]
	 Returns the same array back without change.

	 Passing in Key/Value pairs like a=b;c=d;
	 will return an object { a: "b", c : "d"}

	 Passing any other value will be turned into a string with toString()
	 and handled as if it was an array.  Passing a number 1234 will return ["1234"]

	 Passing a null value will return an empty array [];
	 @param value A value to make options from
	 @returns A value for the options

	 */
	static getOptions(value: any): any
	{
		if (value === null)
		{
			return [];
		}

		if (typeof value === 'object' && value.length != undefined)
		{
			return value;
		}

		// check if value is object
		if (typeof value === 'object')
		{
			const result: any = {};
			for (const key of Object.keys(value))
			{
				result[DataTools.toSnakeString(key)] = value[key];
			}

			return result;
		}

		if (typeof value === 'string')
		{
			//  JSON Object
			if (value.length > 0 && value.charAt(0) === '{')
			{
				try
				{
					const raw = DataTools.fromJSON(value);

					// Only return the version with lowercase /snake case values.
					const result: any = {};
					for (const key of Object.keys(raw))
					{
						result[DataTools.toSnakeString(key)] = raw[key];
					}

					return result;
				} catch {
					return [value.trim()]
				}
			}

			// JSON Array
			if (value.length > 0 && value.charAt(0) === '[')
			{
				try
				{
					const list = DataTools.fromJSON(value);
					return list;
				} catch {
					const text = value.trim().replace(/^\[/, '').replace(/\]$/, '');
					const list = text.split(',');
					const options: string[] = [];
					for (const rawItem of list)
					{
						options.push(rawItem.trim());
					}
					return options;
				}
			}

			if (/=/.test(value))
			{
				let options: any = {};
				value.split(/[;,]/).forEach((pair: string) =>
				{
					if (/=/.test(pair))
					{
						const parts = pair.split('=');
						let keyValue = DataTools.toSnakeString(parts[0]);
						let value = parts[1].trim();
						options[keyValue] = value;
					} else
					{
						if (pair && pair.length > 0) options[DataTools.toSnakeString(pair)] = true;
					}
				});
				return options;
			}
		}

		// Any other text value
		const rawItems = value.toString().split(',');
		const options: string[] = [];
		for (const rawItem of rawItems)
		{
			options.push(rawItem.trim().toLocaleLowerCase());
		}

		return options;
	}

	/**
	 * Simple snake string function, similar to lodash without the
	 * extra crap that lodash adds.
	 * @param value A value to convert snake case
	 * @returns A snake case string
	 */
	static toSnakeString(value: string | undefined): string
	{
		if (!value) return '';
		let snake = value.trim();
		snake = snake.toLocaleLowerCase();
		snake = snake.replace(/\s/g, '_');
		return snake;
	}

	/**
	 * Takes in some piece of data and returns a string
	 * @param data
	 */
	static internalValidateString(data: any): string
	{
		if (data === undefined) return '';
		if (data === null) return '';

		if (typeof data === 'string') return data;

		if (typeof data === 'boolean')
		{
			if (data) return 'Yes';
			return 'No';
		}

		if (typeof data === 'number') return data.toString();

		if (typeof data === 'object' && data.toISOString) return data.toISOString();

		if (data.toString) return data.toString();

		console.log('DataTools internalValidateString unable to find string from ', data);
		return '';
	}

	/**
	 * Check if value is year value - number between 1900 and 2100
	 * @param value A value to check if is year value
	 * @returns A true if the value is year, false if the value is not year
	 */
	static isYearValue(value: any): boolean
	{
		// convert value to string and check if the length is 4
		const strValue = this.internalValidateString(value);
		if (strValue.length !== 4) return false;
		// convert strValue to number
		const numValue = parseInt(strValue);
		// check if value is between 1900 and 2100
		if (numValue < 1900 || numValue > 2100) return false;

		return true;
	}

	/**
	 * Check if value is Date
	 * @param value A value to check if it is Date or Date string
	 * @returns A true if value is Date value
	 */
	static isDate(value: any): boolean
	{
		// use getMoment to get Date object
		// console.log("TEST[", value, "]=", this.getMoment(value));
		if (this.getMoment(value) === null) return false;
		return true;
	}

	/**
	 * Get operators for less_than, greater_than
	 * @param leftValue A value for the left operator
	 * @param rightValue A value for the right operator
	 * @returns An array of numbers for the operators
	 */
	static getOperators(leftValue: any, rightValue: any): (number | null)[]
	{
		let sourceNumber = this.getNumber(leftValue);
		let targetNumber = this.getNumber(rightValue);

		let df = new DataFormatter();
		// check if leftValue and rightValue are Date
		if (this.isDate(leftValue) && this.isDate(rightValue))
		{
			return [sourceNumber, targetNumber];
		}
		// check if leftValue is Date
		if (this.isDate(leftValue))
		{
			sourceNumber = df.unformatData('Year', leftValue);
			// check if rightValue is Year value
			if (!this.isYearValue(rightValue)) return [sourceNumber, null];
		}
		// check if rightValue is Date
		if (this.isDate(rightValue))
		{
			targetNumber = df.unformatData('Year', rightValue);
			// check if leftValue is Year value
			if (!this.isYearValue(leftValue)) return [null, targetNumber];
		}

		return [sourceNumber, targetNumber];
	}

	/**
	 * Generate Random String
	 *
	 * @param length
	 */
	static generateRandomStr(length: number = 8)
	{
		let letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
		let code = '';
		for (let x = 0; x < length; x++)
		{
			let num = Math.floor(Math.random() * letters.length);
			code += letters.charAt(num);
		}
		return code;
	}
}
